import React, { useState, useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import {
  makeReservation,
  createPayment,
  paymentExternal,
  govPayLogin,
} from "../Services";
import { toast } from "react-toastify";
import GuestTicketModal from "./GuestTicketModal";

// Safely read label or fallback to value/string
function labelOrValue(x) {
  return x && typeof x === "object" && x.label ? x.label : x || "";
}

// JWT helpers
function getExpiryFromJWT(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.exp) return payload.exp * 1000;
  } catch (e) {}
  return null;
}
async function ensureGovPayToken(loginPayload) {
  let token = localStorage.getItem("govpay_token");
  let expiry = localStorage.getItem("govpay_token_expiry");
  if (token && expiry && Date.now() < Number(expiry)) return token;

  const response = await govPayLogin(loginPayload);
  token = response.token;
  const expiresIn = response.expires_in;
  const expiryTime = expiresIn
    ? Date.now() + expiresIn * 1000
    : getExpiryFromJWT(token) || Date.now() + 86400 * 1000;
  localStorage.setItem("govpay_token", token);
  localStorage.setItem("govpay_token_expiry", expiryTime);
  return token;
}

// Extract reference from varying shapes
function extractWangovRef(resp) {
  if (!resp) return "";
  if (resp.application_number) return String(resp.application_number);
  if (resp.reference) return String(resp.reference);
  if (resp.data && (resp.data.application_number || resp.data.reference)) {
    return String(resp.data.application_number || resp.data.reference);
  }
  if (
    resp.result &&
    (resp.result.application_number || resp.result.reference)
  ) {
    return String(resp.result.application_number || resp.result.reference);
  }
  return "";
}

const Payment = (props) => {
  const generateApplicationNumber = () => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    // This creates a highly unique alphanumeric string.
    // It combines milliseconds since epoch with a random slice.
    // The chance of a collision is astronomically low.
    const uniquePart =
      Date.now().toString(36) + Math.random().toString(36).substring(2, 9);

    // We take the last 6 characters and convert to uppercase for a clean, unique ID.
    const randomPart = uniquePart.slice(-6).toUpperCase();

    return `FST-${year}${month}${day}-${randomPart}`;
  };

  // The useRef hook is no longer needed with the on('close') event.
  // const finalizedOnce = useRef(false);

  const [state, setState] = useState({
    errMsg: "",
    showPaymentErr: false,
    showValidateErr: false,
    loading: false,
    applicationNumber: generateApplicationNumber(),
    amount: props.amount || 0,
    total: props.total || 0,
    discount: props.discount || 0,
    from: props.from,
    to: props.to,
    train: props.train,
    trainClass: props.trainClass,
    time: props.time,
    qty: props.qty,
    date: props.date,
    phoneNo: props.phoneNo || "",
    email: props.email || "",
    fullname: props.fullname || "",
    transactionCost: props.transactionCost || 0,
    // Embed and post-payment
    wangovRef: "",
    showGuestTicketModal: false,
    guestReservation: null,
    // Confirmation modal
    showConfirm: props.showPaymentModal,
  });

  // Programmatically open the WanGov popup
  // This function is updated to use the new create and on methods.
  const openWanGovProgrammatically = (ref, total) => {
    if (
      !window.WanGov ||
      !window.WanGov.checkout ||
      !window.WanGov.checkout.create
    ) {
      console.error(
        "WanGov SDK is not available. Please ensure the script is loaded."
      );
      toast.error("Payment system not ready. Please refresh and try again.");
      return;
    }

    const fee = Number(total || 0).toFixed(2);
    const options = {
      applicationNumber: ref,
      serviceName: "government-warf-ferry",
      serviceCode: "mta0010",
      serviceFee: `SLE ${fee}`,
      allowedMethods: [
        "VISA / MasterCard",
        "Orange Money",
        "QMoney",
        "Afrimoney",
        "Vult",
      ],
    };

    // Use the new `create` method to get a session.
    const checkout = window.WanGov.checkout.create(options);

    // Set up the listener for when the modal is closed.
    // The callback receives the options object used to create the session.
    checkout.on("close", () => {
      console.log("WanGov checkout modal was closed. Finalizing payment...");
      finalizeAfterPopup(ref);
    });

    // Open the checkout modal.
    checkout.open();
  };

  // Initialize phone from local user
  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      setState((prev) => ({ ...prev, phoneNo: JSON.parse(user).phone }));
    }
  }, []);

  // Step 1: create WanGov bill and get reference, then open popup immediately
  const handleSubmit = async (event) => {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
      event.stopPropagation();
    }

    setState((prev) => ({
      ...prev,
      showPaymentErr: false,
      showValidateErr: false,
      loading: true,
      errMsg: "",
    }));

    try {
      const loginPayload = {
        phone_number: "+23273394066", // Ensure this is correct for your GovPay login
        password: "@tytc3UeNu7NScS", // Ensure this is correct for your GovPay login
        login_type: "self",
      };
      const jwtToken = await ensureGovPayToken(loginPayload);

      const loggedInUser = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : {};

      const applicantFullname =
        (state.fullname || "").trim().length > 0
          ? state.fullname
          : (loggedInUser.fname || "").trim() +
            " " +
            (loggedInUser.lname || "").trim();

      // Normalize phone number: use provided value or fallback to "N/A"
      const normalizedPhone =
        (state.phoneNo || "").trim().length > 0 ? state.phoneNo : "N/A";
      const paymentPayload = {
        phone_number: normalizedPhone,
        amount: Number(state.total),
        applicant_nin: state.applicantNIN || "SL123456789",
        applicant_fullname: applicantFullname,
        application_number: state.applicationNumber, // your local reference
      };

      const response = await paymentExternal(paymentPayload, jwtToken);
      const ref = extractWangovRef(response);

      if (!ref) {
        throw new Error("External service did not return a valid reference.");
      }

      // We no longer need to use `finalizedOnce.current`.
      setState((prev) => ({
        ...prev,
        //loading: false,
        wangovRef: ref,
      }));

      // Call the new function to open the modal programmatically
      openWanGovProgrammatically(ref, state.total);
    } catch (err) {
      console.error("Payment init failed:", err);
      setState((prev) => ({
        ...prev,
        loading: false,
        showPaymentErr: true,
        errMsg:
          "Could not get a WanGov payment reference. Check service configuration.",
      }));
    }
  };

  // Step 2: after popup closes, persist payment and reservation, then show QR
  const finalizeAfterPopup = async (wangovRef) => {
    try {
      const user = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : null;

      const paymentDocPayload = {
        phone_number:
          (state.phoneNo || "").trim().length > 0 ? state.phoneNo : "N/A",
        service_slug: "government-warf-ferry",
        amount: state.total,
        applicant_nin: state.applicantNIN || "SL123456789",
        application_number: state.applicationNumber, // your local reference
        billing_number: wangovRef, // WanGov reference
        status: "Pending",
        api_key: user ? user.fname : state.fullname,
        secret_key: user ? user.lname : "",
      };
      const paymentDoc = await createPayment(paymentDocPayload);

      let maxTrips = 1;
      if (
        state.from &&
        state.from.value &&
        String(state.from.value).toLowerCase().indexOf("full") !== -1
      ) {
        maxTrips = 2;
      }

      const reservation = {
        user: user ? user._id : undefined,
        email: user ? user.email : state.email,
        from: state.from,
        to: state.to,
        train: state.train,
        trainClass: state.trainClass,
        time: state.time,
        qty: state.qty,
        date: state.date,
        amount: state.amount,
        discount: state.discount,
        total: state.total,
        paymentMethod: "WanGov",
        scannedCount: 0,
        maxTrips: maxTrips,
        lastScannedAt: null,
        phone: (state.phoneNo || "").trim().length > 0 ? state.phoneNo : "N/A",
        fullname: state.fullname || (user ? user.fname + " " + user.lname : ""),
        payment: paymentDoc ? paymentDoc._id : undefined,
        paymentStatus: "Pending",
        payment_number: wangovRef,
        applicationNumber: state.applicationNumber,
      };

      const resData = await makeReservation(reservation);

      setState((prev) => ({
        ...prev,
        showGuestTicketModal: true,
        guestReservation: resData,
      }));
    } catch (err) {
      console.error("Finalize after popup failed:", err);
      toast.error("Could not finalize reservation.");
    }
  };

  return (
    <>
      {!state.showGuestTicketModal && props.showModal ? (
        <Modal show={props.showModal} onHide={props.onHide} centered size="md">
          <Modal.Header
            closeButton
            style={{ borderBottom: "none", padding: "24px 24px 0" }}
          >
            <Modal.Title style={{ fontWeight: "700", fontSize: "22px" }}>
              Confirm Your Purchase
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: "8px 24px 24px" }}>
            <div
              style={{
                background: "#f8f9fa",
                borderRadius: "8px",
                padding: "20px",
                border: "1px solid #e9ecef",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                  fontSize: "15px",
                }}
              >
                <span style={{ color: "#6c757d" }}>Route</span>
                <strong style={{ textAlign: "right" }}>
                  {labelOrValue(state.from)} &rarr; {labelOrValue(state.to)}
                </strong>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                  fontSize: "15px",
                }}
              >
                <span style={{ color: "#6c757d" }}>Date & Time</span>
                <strong style={{ textAlign: "right" }}>
                  {state.date} &bull; {state.time}
                </strong>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                  fontSize: "15px",
                }}
              >
                <span style={{ color: "#6c757d" }}>Quantity</span>
                <strong>{state.qty} Ticket(s)</strong>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                  fontSize: "15px",
                }}
              >
                <span style={{ color: "#6c757d" }}>Reference</span>
                <strong>{state.applicationNumber}</strong>
              </div>
              <hr style={{ margin: "16px 0" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: "18px", fontWeight: "600" }}>
                  Total Amount
                </span>
                <strong style={{ fontSize: "24px", color: "#007bff" }}>
                  SLE {Number(state.total).toFixed(2)}
                </strong>
              </div>
            </div>
            <p
              style={{
                marginTop: "20px",
                textAlign: "center",
                color: "#6c757d",
                fontSize: "14px",
              }}
            >
              You will be redirected to WanGov to complete your payment.
            </p>
          </Modal.Body>
          <Modal.Footer
            style={{ borderTop: "none", padding: "0 24px 24px", gap: "8px" }}
          >
            <Button
              variant="outline-secondary"
              onClick={props.onHide}
              disabled={state.loading}
              style={{ padding: "10px 20px" }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={state.loading}
              style={{ padding: "10px 20px" }}
            >
              {state.loading ? "Paying..." : "Yes, Continue"}
            </Button>
          </Modal.Footer>
        </Modal>
      ) : (
        <GuestTicketModal
          show={state.showGuestTicketModal}
          onHide={() => {
            setState((prev) => ({ ...prev, showGuestTicketModal: false }));
          }}
          reservation={state.guestReservation}
          onPurchaseComplete={props.onPurchaseComplete}
          phoneNo={state.phoneNo || ""}
          email={state.email || ""}
          fullname={state.fullname || ""}
          applicationNumber={state.applicationNumber || ""}
          transactionCost={state.transactionCost || 0}
        />
      )}
    </>
  );
};

export default Payment;
